import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { ActivityEventDetails } from '@shared/services/activities_events/types/activitiesEvents.types';
import type { GetActivitiesEventsFiltersResponse } from '@shared/services/activities_events/types/activitiesEventsFilters.type';

import { initialState } from './initialState';
import type { BookedActivitiesTab } from '../pages/BookedActivitiesPage/types';
import type { FilterOptionType, ISelectedFilters } from '../types';

const activitiesEventsSlice = createSlice({
  initialState,
  name: 'ActivitiesEvents',
  reducers: {
    setFilterPageState: (
      state,
      action: PayloadAction<{
        filterType: FilterOptionType;
        options: null | number | string | string[];
      }>
    ) => {
      const { filterType, options } = action.payload;
      state.filterPageState[filterType] = options as never;
    },
    setFilterPageStateFromApplied: (state, action: PayloadAction<ISelectedFilters>) => {
      state.filterPageState = action.payload;
    },
    setAppliedFilters: (state, action: PayloadAction<ISelectedFilters>) => {
      state.appliedFilters = action.payload;
    },
    setAppliedFilter: (
      state,
      action: PayloadAction<{
        filterType: FilterOptionType;
        options: null | number | string | string[];
      }>
    ) => {
      const { filterType, options } = action.payload;
      state.appliedFilters[filterType] = options as never;
    },
    resetFilterPageStateExceptKeyword: (state) => {
      state.filterPageState = {
        ...initialState.filterPageState,
        keyword: state.filterPageState.keyword,
      };
    },
    setActivityTypeFilterSelection: (state, action: PayloadAction<string[]>) => {
      state.activityTypeFilterSelection = action.payload;
    },
    clearActivityTypeFilterSelection: (state) => {
      state.activityTypeFilterSelection = initialState.activityTypeFilterSelection;
    },
    setFilterOptions: (state, action: PayloadAction<GetActivitiesEventsFiltersResponse>) => {
      state.filterOptions = action.payload;
    },
    setBookingError: (state, action: PayloadAction<string | undefined>) => {
      state.bookingError = action.payload;
    },
    clearBookingError: (state) => {
      state.bookingError = undefined;
    },
    setBookedActivityEvent: (state, action: PayloadAction<ActivityEventDetails>) => {
      state.bookedActivityEvent = action.payload;
    },
    clearBookedActivityEvent: (state) => {
      state.bookedActivityEvent = initialState.bookedActivityEvent;
    },
    setCurrentBookedTabKey: (state, action: PayloadAction<BookedActivitiesTab>) => {
      state.currentTab.bookedTabKey = action.payload;
    },
    setCurrentOtherSessionsDateTabKey: (state, action: PayloadAction<{ date: string; eventId: string }>) => {
      state.currentTab.otherSessions = {
        dateTabKey: action.payload.date,
        eventId: action.payload.eventId,
      };
    },
    setCurrentLandingDateTabKey: (state, action: PayloadAction<string>) => {
      state.currentTab.landingDateTabKey = action.payload;
    },
  },
});

export const { actions: activitiesEventsAction } = activitiesEventsSlice;

export default activitiesEventsSlice.reducer;
